// @ts-nocheck
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useAppState } from '../store';
import * as api from '../api';
import type { Column } from '../types';

interface Props {
  column: Column;
  itemId: string;
  value: string;
}

export default function CellRenderer({ column, itemId, value }: Props) {
  const dispatch = useDispatch();
  const state = useAppState();

  const parsedValue = (() => {
    try { return value ? JSON.parse(value) : ''; }
    catch { return value || ''; }
  })();

  const config = (() => {
    try { return typeof column.config === 'string' ? JSON.parse(column.config) : column.config; }
    catch { return {}; }
  })();

  const updateCell = async (newValue: any) => {
    const strValue = JSON.stringify(newValue);
    dispatch({ type: 'SET_CELL', item_id: itemId, column_id: column.id, value: strValue });
    await api.updateCells([{ item_id: itemId, column_id: column.id, value: newValue }]);
  };

  switch (column.type) {
    case 'status':
      return <StatusCell value={parsedValue} config={config} onChange={updateCell} />;
    case 'priority':
      return <PriorityCell value={parsedValue} config={config} onChange={updateCell} />;
    case 'text':
      return <TextInputCell value={parsedValue} onChange={updateCell} />;
    case 'numbers':
      return <NumberCell value={parsedValue} onChange={updateCell} />;
    case 'date':
      return <DateCell value={parsedValue} onChange={updateCell} />;
    case 'dropdown':
      return <DropdownCell value={parsedValue} config={config} onChange={updateCell} />;
    case 'checkbox':
      return <CheckboxCell value={parsedValue} onChange={updateCell} />;
    case 'email':
      return <TextInputCell value={parsedValue} onChange={updateCell} type="email" placeholder="email@..." />;
    case 'phone':
      return <TextInputCell value={parsedValue} onChange={updateCell} type="tel" placeholder="Phone..." />;
    case 'country':
      return <CountryCell value={parsedValue} onChange={updateCell} />;
    case 'people':
      return <PeopleCell value={parsedValue} users={state.user ? [state.user] : []} onChange={updateCell} />;
    case 'timeline':
      return <TimelineCell value={parsedValue} onChange={updateCell} />;
    case 'files':
      return <div className="cell" style={{ color: '#676879', fontSize: 13 }}>{parsedValue ? 'File attached' : '—'}</div>;
    case 'website':
      return <WebsiteCell value={parsedValue} onChange={updateCell} />;
    case 'formula':
      return <div className="formula-cell">{parsedValue || 'fx'}</div>;
    default:
      return <TextInputCell value={parsedValue} onChange={updateCell} />;
  }
}

// ──────────────────── STATUS CELL ────────────────────
function StatusCell({ value, config, onChange }: { value: string; config: any; onChange: (v: any) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const labels = config.labels || {};
  const colors = config.colors || {};

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          pickerRef.current && !pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const rect = ref.current?.getBoundingClientRect();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        className="status-cell"
        style={{ backgroundColor: colors[value] || '#c4c4c4', cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
      >
        {labels[value] || ''}
      </div>
      {open && rect && createPortal(
        <div ref={pickerRef} className="status-picker" style={{
          position: 'fixed', left: rect.left, top: rect.bottom + 2, zIndex: 9990,
        }}>
          {Object.entries(labels).map(([key, label]) => (
            <button
              key={key}
              className="status-option"
              style={{ backgroundColor: colors[key] || '#c4c4c4' }}
              onClick={() => { onChange(key); setOpen(false); }}
            >
              {label as string || '(empty)'}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ──────────────────── PRIORITY CELL ────────────────────
function PriorityCell({ value, config, onChange }: { value: string; config: any; onChange: (v: any) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const labels = config.labels || {};
  const colors = config.colors || {};

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          pickerRef.current && !pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const rect = ref.current?.getBoundingClientRect();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div
        className="priority-cell"
        style={{ backgroundColor: colors[value] || '#c4c4c4', cursor: 'pointer' }}
        onClick={() => setOpen(!open)}
      >
        {labels[value] || ''}
      </div>
      {open && rect && createPortal(
        <div ref={pickerRef} className="status-picker" style={{
          position: 'fixed', left: rect.left, top: rect.bottom + 2, zIndex: 9990,
        }}>
          {Object.entries(labels).map(([key, label]) => (
            <button
              key={key}
              className="status-option"
              style={{ backgroundColor: colors[key] || '#c4c4c4' }}
              onClick={() => { onChange(key); setOpen(false); }}
            >
              {label as string || '(empty)'}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ──────────────────── TEXT INPUT CELL ────────────────────
function TextInputCell({ value, onChange, type = 'text', placeholder = '' }: {
  value: string; onChange: (v: any) => void; type?: string; placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(String(value || ''));

  const save = () => {
    if (text !== String(value || '')) onChange(text);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="cell">
        <input
          className="cell-input"
          type={type}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          placeholder={placeholder}
        />
      </div>
    );
  }

  return (
    <div className="cell" onClick={() => { setText(String(value || '')); setEditing(true); }}>
      {value || <span style={{ color: '#c4c4c4' }}>{placeholder || '—'}</span>}
    </div>
  );
}

// ──────────────────── NUMBER CELL ────────────────────
function NumberCell({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  const [editing, setEditing] = useState(false);
  const [num, setNum] = useState(String(value ?? ''));

  const save = () => {
    const parsed = num === '' ? '' : Number(num);
    if (parsed !== value) onChange(parsed);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="cell">
        <input
          className="cell-input"
          type="number"
          autoFocus
          value={num}
          onChange={(e) => setNum(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        />
      </div>
    );
  }

  return (
    <div className="cell" onClick={() => { setNum(String(value ?? '')); setEditing(true); }}>
      {value !== '' && value !== null && value !== undefined ? value : <span style={{ color: '#c4c4c4' }}>—</span>}
    </div>
  );
}

// ──────────────────── DATE CELL ────────────────────
function DateCell({ value, onChange }: { value: string; onChange: (v: any) => void }) {
  const dateStr = value ? value.substring(0, 10) : '';
  return (
    <div className="cell date-cell">
      <input
        type="date"
        value={dateStr}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

// ──────────────────── DROPDOWN CELL ────────────────────
function DropdownCell({ value, config, onChange }: { value: any; config: any; onChange: (v: any) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const options: string[] = config.options || [];
  const selected: string[] = Array.isArray(value) ? value : value ? [value] : [];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          pickerRef.current && !pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const toggle = (opt: string) => {
    const newVal = selected.includes(opt)
      ? selected.filter((s) => s !== opt)
      : [...selected, opt];
    onChange(newVal);
  };

  const rect = ref.current?.getBoundingClientRect();

  return (
    <div ref={ref} className="dropdown-cell" style={{ position: 'relative' }}>
      <div className="dropdown-tags" onClick={() => setOpen(!open)} style={{ cursor: 'pointer', minHeight: 32 }}>
        {selected.length > 0 ? (
          selected.map((s) => <span key={s} className="dropdown-tag">{s}</span>)
        ) : (
          <span style={{ color: '#c4c4c4', fontSize: 13 }}>—</span>
        )}
      </div>
      {open && rect && createPortal(
        <div ref={pickerRef} className="dropdown-picker" style={{
          position: 'fixed', left: rect.left, top: rect.bottom + 2, zIndex: 9990,
        }}>
          {options.map((opt) => (
            <button
              key={opt}
              className={`dropdown-option ${selected.includes(opt) ? 'selected' : ''}`}
              onClick={() => toggle(opt)}
            >
              {selected.includes(opt) ? '✓ ' : ''}{opt}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ──────────────────── CHECKBOX CELL ────────────────────
function CheckboxCell({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  return (
    <div className="checkbox-cell">
      <input
        type="checkbox"
        checked={!!value}
        onChange={(e) => onChange(e.target.checked)}
      />
    </div>
  );
}

// ──────────────────── COUNTRY CELL ────────────────────
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Argentina', 'Australia', 'Austria', 'Belgium',
  'Brazil', 'Canada', 'Chile', 'China', 'Colombia', 'Czech Republic', 'Denmark',
  'Egypt', 'Finland', 'France', 'Germany', 'Greece', 'India', 'Indonesia', 'Ireland',
  'Israel', 'Italy', 'Japan', 'Kenya', 'Malaysia', 'Mexico', 'Netherlands', 'New Zealand',
  'Nigeria', 'Norway', 'Pakistan', 'Peru', 'Philippines', 'Poland', 'Portugal',
  'Romania', 'Russia', 'Saudi Arabia', 'Singapore', 'South Africa', 'South Korea',
  'Spain', 'Sweden', 'Switzerland', 'Thailand', 'Turkey', 'UAE', 'UK', 'USA',
  'Ukraine', 'Vietnam',
];

function CountryCell({ value, onChange }: { value: string; onChange: (v: any) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          pickerRef.current && !pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = COUNTRIES.filter((c) => c.toLowerCase().includes(search.toLowerCase()));
  const rect = ref.current?.getBoundingClientRect();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className="country-cell" onClick={() => setOpen(!open)} style={{ cursor: 'pointer' }}>
        {value ? value : <span style={{ color: '#c4c4c4' }}>—</span>}
      </div>
      {open && rect && createPortal(
        <div ref={pickerRef} className="dropdown-picker" style={{
          position: 'fixed', left: rect.left, top: rect.bottom + 2, zIndex: 9990, maxHeight: 200, overflow: 'auto',
        }}>
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            style={{ width: '100%', padding: '6px 8px', border: '1px solid #e6e9ef', borderRadius: 4, marginBottom: 4, outline: 'none', fontSize: 13 }}
          />
          {filtered.map((c) => (
            <button
              key={c}
              className={`dropdown-option ${c === value ? 'selected' : ''}`}
              onClick={() => { onChange(c); setOpen(false); setSearch(''); }}
            >
              {c}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ──────────────────── PEOPLE CELL ────────────────────
function PeopleCell({ value, users, onChange }: { value: any; users: any[]; onChange: (v: any) => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const pickerRef = useRef<HTMLDivElement>(null);
  const selected: string[] = Array.isArray(value) ? value : value ? [value] : [];

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node) &&
          pickerRef.current && !pickerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const rect = ref.current?.getBoundingClientRect();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div className="people-cell" onClick={() => setOpen(!open)} style={{ cursor: 'pointer', minHeight: 32 }}>
        {selected.length > 0 ? (
          selected.map((uid) => {
            const user = users.find((u) => u.id === uid);
            const initials = user?.name?.split(' ').map((w: string) => w[0]).join('').substring(0, 2) || '?';
            return (
              <div key={uid} className="people-avatar" title={user?.name || uid}>
                {user?.avatar_url ? (
                  <img src={user.avatar_url} alt="" style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                ) : initials}
              </div>
            );
          })
        ) : (
          <span style={{ color: '#c4c4c4', fontSize: 13, padding: '0 4px' }}>—</span>
        )}
      </div>
      {open && rect && createPortal(
        <div ref={pickerRef} className="dropdown-picker" style={{
          position: 'fixed', left: rect.left, top: rect.bottom + 2, zIndex: 9990,
        }}>
          {users.map((u) => (
            <button
              key={u.id}
              className={`dropdown-option ${selected.includes(u.id) ? 'selected' : ''}`}
              onClick={() => {
                const newVal = selected.includes(u.id)
                  ? selected.filter((id) => id !== u.id)
                  : [...selected, u.id];
                onChange(newVal);
              }}
            >
              {selected.includes(u.id) ? '✓ ' : ''}{u.name}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

// ──────────────────── WEBSITE CELL ────────────────────
function WebsiteCell({ value, onChange }: { value: string; onChange: (v: any) => void }) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(String(value || ''));

  const save = () => {
    if (text !== String(value || '')) onChange(text);
    setEditing(false);
  };

  if (editing) {
    return (
      <div className="cell">
        <input
          className="cell-input"
          type="url"
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={save}
          onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
          placeholder="https://..."
        />
      </div>
    );
  }

  if (value) {
    const url = value.startsWith('http') ? value : `https://${value}`;
    const display = value.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '');
    return (
      <div className="cell" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          style={{ color: '#0073ea', fontSize: 13, textDecoration: 'none', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
        >
          {display}
        </a>
        <button
          onClick={() => { setText(String(value || '')); setEditing(true); }}
          style={{ color: '#676879', fontSize: 11, padding: '0 4px', flexShrink: 0 }}
        >
          ✎
        </button>
      </div>
    );
  }

  return (
    <div className="cell" onClick={() => { setText(''); setEditing(true); }} style={{ cursor: 'pointer' }}>
      <span style={{ color: '#c4c4c4' }}>—</span>
    </div>
  );
}

// ──────────────────── TIMELINE CELL ────────────────────
function TimelineCell({ value, onChange }: { value: any; onChange: (v: any) => void }) {
  const [editing, setEditing] = useState(false);
  const start = value?.start || '';
  const end = value?.end || '';

  if (editing) {
    return (
      <div className="timeline-cell">
        <div className="timeline-inputs">
          <input
            type="date"
            value={start}
            onChange={(e) => onChange({ start: e.target.value, end })}
          />
          <span style={{ fontSize: 12, color: '#676879' }}>to</span>
          <input
            type="date"
            value={end}
            onChange={(e) => onChange({ start, end: e.target.value })}
          />
          <button onClick={() => setEditing(false)} style={{ fontSize: 12, color: '#676879' }}>Done</button>
        </div>
      </div>
    );
  }

  if (start && end) {
    return (
      <div className="timeline-cell" onClick={() => setEditing(true)} style={{ cursor: 'pointer' }}>
        <div className="timeline-bar" />
        <div className="timeline-dates">
          {new Date(start).toLocaleDateString()} - {new Date(end).toLocaleDateString()}
        </div>
      </div>
    );
  }

  return (
    <div className="cell" onClick={() => setEditing(true)} style={{ cursor: 'pointer' }}>
      <span style={{ color: '#c4c4c4' }}>Set timeline</span>
    </div>
  );
}
